import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bank, CreditCard, Copy } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";
import { SingleUpload } from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-2 text-sm py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium flex items-center gap-2">
        <span className="font-mono">{value}</span>
        <button type="button" onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copiado"); }} className="text-muted-foreground hover:text-foreground"><Copy size={14} /></button>
      </span>
    </div>
  );
}

export default function RenewDialog({ open, onOpenChange, empresa, precio, onDone }) {
  const [tab, setTab] = useState("transferencia");
  const [info, setInfo] = useState(null);
  const [comprobante, setComprobante] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) { setTab("transferencia"); setComprobante(""); api.get("/company/pago-info").then((r) => setInfo(r.data)).catch(() => setInfo(null)); } }, [open]);

  const pagarTransferencia = async () => {
    if (!comprobante) return toast.error("Sube el comprobante de transferencia");
    setLoading(true);
    try { await api.post("/company/renovar", { metodo: "transferencia", comprobante }); toast.success("Licencia renovada 12 meses"); onOpenChange(false); onDone?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  const pagarTarjeta = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/company/mp-preference", { origin: window.location.origin });
      window.location.assign(data.checkout_url);
    } catch (e) { toast.error(errMsg(e)); setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-lg max-w-md" data-testid="renew-dialog">
        <DialogHeader><DialogTitle>Renovar licencia</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{empresa} · {money(precio || 2000)} / año</p>

        <div className="flex gap-1 border-b border-border mt-2">
          <button data-testid="tab-transferencia" onClick={() => setTab("transferencia")}
            className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 flex items-center gap-1.5 ${tab === "transferencia" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>
            <Bank size={16} /> Transferencia
          </button>
          <button data-testid="tab-tarjeta" onClick={() => setTab("tarjeta")}
            className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 flex items-center gap-1.5 ${tab === "tarjeta" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>
            <CreditCard size={16} /> Tarjeta
          </button>
        </div>

        {tab === "transferencia" ? (
          <div className="mt-3 space-y-3">
            {info && (info.banco || info.clabe) ? (
              <div className="bg-secondary rounded-lg p-3">
                <Row label="Banco" value={info.banco} />
                <Row label="Titular" value={info.titular} />
                <Row label="CLABE" value={info.clabe} />
                <Row label="Cuenta" value={info.cuenta} />
                {info.instrucciones && <p className="text-xs text-muted-foreground mt-2">{info.instrucciones}</p>}
              </div>
            ) : (
              <p className="text-xs text-amber-600">Aún no hay datos bancarios configurados. Contacta al proveedor.</p>
            )}
            <SingleUpload value={comprobante} onChange={setComprobante} label="Comprobante de transferencia" testId="renew-comprobante" />
            <Button className="w-full rounded-lg" data-testid="renew-transferencia-btn" disabled={loading} onClick={pagarTransferencia}>
              {loading ? "Enviando…" : "Confirmar renovación"}
            </Button>
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            {info?.mp_habilitado ? (
              <>
                <p className="text-sm text-muted-foreground">Serás redirigido a Mercado Pago para pagar de forma segura. Al aprobarse, tu licencia se renueva 12 meses automáticamente.</p>
                <Button className="w-full rounded-lg gap-2" data-testid="renew-tarjeta-btn" disabled={loading} onClick={pagarTarjeta}>
                  <CreditCard size={16} /> {loading ? "Abriendo checkout…" : "Pagar con tarjeta"}
                </Button>
              </>
            ) : (
              <p className="text-sm text-amber-600">El pago con tarjeta aún no está habilitado. El proveedor debe configurar Mercado Pago.</p>
            )}
          </div>
        )}

        <DialogFooter><Button variant="outline" className="rounded-lg" onClick={() => onOpenChange(false)}>Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
